/** The `growing_stock` kind's options (backend `viewer/report_kinds.py`, GROWING_STOCK_OPTIONS) as the page reads them. */
export type GrowingSection = 'summary' | 'chart' | 'map' | 'units' | 'mls' | 'canopy' | 'plan';
export const GROWING_SECTIONS: GrowingSection[] = ['summary', 'chart', 'map', 'units', 'mls', 'canopy', 'plan'];

/** The kinds of unit, top down; `area` is the whole of it, `mls` a plot of the mobile scan and `field` an area the field crew surveyed, beside the plan's. */
export const LEVELS = ['area', 'lhc', 'odd', 'dil', 'por', 'psk', 'mls', 'field'] as const;
export type Level = (typeof LEVELS)[number];

/** The estimation methods growing_stock.json may hold, in the order the page lists them. */
export const METHODS = ['ortho_visible', 'ortho_under', 'ortho_all', 'mls', 'mls_ortho', 'plan', 'field'] as const;
export type Method = (typeof METHODS)[number];
const DEFAULT_METHODS: readonly Method[] = ['ortho_all', 'mls_ortho', 'plan'];

export interface GrowingStockConfig {
  sections: Record<Exclude<GrowingSection, 'canopy'>, { enabled: boolean }> & {
    /** Canopy cover per unit; `byGenus` splits it into each genus's share (off unless chosen). */
    canopy: { enabled: boolean; byGenus: boolean };
  };
  methods: Record<Method, boolean>;
  /** The chart and the table open per hectare. */
  perHa: boolean;
  /** The kind of unit the page opens on. */
  level: Level;
}

const isObject = (v: unknown): v is Record<string, unknown> => v !== null && typeof v === 'object' && !Array.isArray(v);

/** The stored options over the defaults: a report saved before a section or a method existed still shows it. */
export function growingStockConfig(raw: unknown): GrowingStockConfig {
  const r = isObject(raw) ? raw : {};
  const stored = isObject(r.sections) ? r.sections : {};
  const sections = Object.fromEntries(
    GROWING_SECTIONS.map((s) => {
      const v = stored[s];
      return [s, { enabled: isObject(v) && typeof v.enabled === 'boolean' ? v.enabled : true }];
    }),
  ) as GrowingStockConfig['sections'];
  const canopy = stored.canopy;
  sections.canopy.byGenus = isObject(canopy) && canopy.by_genus === true;
  const chosen = isObject(r.methods) ? r.methods : {};
  const methods = Object.fromEntries(METHODS.map((m) => [m, typeof chosen[m] === 'boolean' ? chosen[m] : DEFAULT_METHODS.includes(m)])) as GrowingStockConfig['methods'];
  const level = (LEVELS as readonly unknown[]).includes(r.level) ? (r.level as Level) : 'area';
  return { sections, methods, perHa: r.per_ha === true, level };
}
